// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '../api/client'
import toast from 'react-hot-toast'
import { confirmDialog } from '../components/DialogHost'
import { ArrowLeft, Send, Paperclip, CheckCircle, AlertCircle, Loader2, User, X, Download } from 'lucide-react'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = [
  'image/png', 'image/jpeg', 'image/gif',
  'application/pdf',
  'text/plain', 'text/csv',
  'application/zip', 'application/x-zip-compressed',
  'application/json',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]

interface Comment {
  id: string
  author_type: string
  author_name: string | null
  content: string
  created_at: string
  attachments: { id: string; filename: string; file_size: number }[]
}

interface Ticket {
  id: string
  ticket_number: string
  subject: string
  description: string
  category: string
  priority: string
  status: string
  assigned_to: string | null
  created_at: string
  updated_at: string
  resolved_at: string | null
  comments: Comment[]
  attachments: { id: string; filename: string; file_size: number }[]
}

export default function TicketDetail() {
  const { ticketId } = useParams()
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadTicket()
  }, [ticketId])

  const loadTicket = async () => {
    try {
      const res = await api.get(`/api/portal/support/tickets/${ticketId}`)
      setTicket(res.data)
    } catch (error) {
      toast.error('Failed to load ticket')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles: File[] = []

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 10MB limit`)
        continue
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name} has unsupported file type`)
        continue
      }
      validFiles.push(file)
    }

    setSelectedFiles(prev => [...prev, ...validFiles])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleDownloadAttachment = async (attachmentId: string, filename: string) => {
    try {
      const res = await api.get(`/api/portal/support/tickets/${ticketId}/attachments/${attachmentId}`)
      const downloadUrl = res.data.download_url

      // Open the download URL
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      toast.error('Failed to download attachment')
    }
  }

  const handleSendComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newComment.trim() && selectedFiles.length === 0) return

    setIsSending(true)
    try {
      // Send comment if there's text
      if (newComment.trim()) {
        await api.post(`/api/portal/support/tickets/${ticketId}/comments`, {
          content: newComment
        })
      }

      // Upload any selected files
      if (selectedFiles.length > 0) {
        setIsUploading(true)
        for (const file of selectedFiles) {
          const formData = new FormData()
          formData.append('file', file)
          try {
            await api.post(`/api/portal/support/tickets/${ticketId}/attachments`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            })
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}:`, uploadError)
            toast.error(`Failed to upload ${file.name}`)
          }
        }
        setIsUploading(false)
      }

      setNewComment('')
      setSelectedFiles([])
      toast.success(newComment.trim() ? 'Reply sent' : 'File(s) uploaded')
      loadTicket()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send reply')
    } finally {
      setIsSending(false)
      setIsUploading(false)
    }
  }

  const handleCloseTicket = async () => {
    if (!(await confirmDialog({ title: 'Close ticket', message: 'Are you sure you want to close this ticket?', confirmText: 'Close ticket' }))) return

    try {
      await api.put(`/api/portal/support/tickets/${ticketId}`, { status: 'closed' })
      toast.success('Ticket closed')
      loadTicket()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to close ticket')
    }
  }

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      open: 'badge-info',
      in_progress: 'badge-warning',
      waiting_customer: 'badge-warning',
      resolved: 'badge-success',
      closed: 'badge-success'
    }
    return classes[status] || ''
  }

  const getPriorityBadge = (priority: string) => {
    const classes: Record<string, string> = {
      low: 'bg-gray-500/20 text-gray-400',
      medium: 'bg-blue-500/20 text-blue-400',
      high: 'bg-orange-500/20 text-orange-400',
      critical: 'bg-red-500/20 text-red-400'
    }
    return classes[priority] || ''
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Ticket Not Found</h2>
        <Link to="/support" className="text-primary-500 hover:underline">
          Back to Support
        </Link>
      </div>
    )
  }

  const isClosed = ticket.status === 'closed'

  return (
    <div className="space-y-6 fade-in max-w-4xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/support" className="mt-1">
          <ArrowLeft className="w-5 h-5 text-gray-400 hover:text-white" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono text-sm text-primary-500">{ticket.ticket_number}</span>
            <span className={`badge ${getStatusBadge(ticket.status)}`}>
              {ticket.status.replace('_', ' ')}
            </span>
            <span className={`badge ${getPriorityBadge(ticket.priority)}`}>
              {ticket.priority}
            </span>
          </div>
          <h1 className="text-2xl font-bold">{ticket.subject}</h1>
        </div>
        {!isClosed && (
          <button onClick={handleCloseTicket} className="btn btn-secondary">
            Close Ticket
          </button>
        )}
      </div>

      {/* Ticket Info */}
      <div className="card">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Category</p>
            <p className="capitalize">{ticket.category.replace('_', ' ')}</p>
          </div>
          <div>
            <p className="text-gray-400">Created</p>
            <p>{new Date(ticket.created_at).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-400">Updated</p>
            <p>{new Date(ticket.updated_at).toLocaleString()}</p>
          </div>
          {ticket.assigned_to && (
            <div>
              <p className="text-gray-400">Assigned To</p>
              <p>{ticket.assigned_to}</p>
            </div>
          )}
        </div>
      </div>

      {/* Original Description */}
      <div className="card">
        <h3 className="font-semibold mb-3">Description</h3>
        <div className="prose prose-invert max-w-none">
          <p className="whitespace-pre-wrap text-gray-300">{ticket.description}</p>
        </div>
        {ticket.attachments.length > 0 && (
          <div className="mt-4 pt-4 border-t border-dark-border">
            <h4 className="text-sm font-medium mb-2">Attachments</h4>
            <div className="flex flex-wrap gap-2">
              {ticket.attachments.map((att) => (
                <button
                  key={att.id}
                  onClick={() => handleDownloadAttachment(att.id, att.filename)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg text-sm hover:bg-gray-700"
                >
                  <Download className="w-4 h-4" />
                  {att.filename}
                  <span className="text-xs text-gray-500">({formatFileSize(att.file_size)})</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Comments */}
      <div className="card">
        <h3 className="font-semibold mb-4">Conversation</h3>
        <div className="space-y-4">
          {ticket.comments.length > 0 ? (
            ticket.comments.map((comment) => (
              <div
                key={comment.id}
                className={`p-4 rounded-lg ${
                  comment.author_type === 'customer'
                    ? 'bg-primary-500/10 ml-8'
                    : 'bg-gray-800 mr-8'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    comment.author_type === 'customer' ? 'bg-primary-500/20' : 'bg-green-500/20'
                  }`}>
                    <User className={`w-4 h-4 ${
                      comment.author_type === 'customer' ? 'text-primary-500' : 'text-green-500'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      {comment.author_name || (comment.author_type === 'customer' ? 'You' : 'Support Team')}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(comment.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="whitespace-pre-wrap text-gray-300">{comment.content}</p>
                {comment.attachments.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {comment.attachments.map((att) => (
                      <button
                        key={att.id}
                        onClick={() => handleDownloadAttachment(att.id, att.filename)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700/50 rounded text-xs hover:bg-gray-700"
                      >
                        <Download className="w-3 h-3" />
                        {att.filename}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-400 text-center py-4">No replies yet</p>
          )}
        </div>
      </div>

      {/* Reply Form */}
      {!isClosed && (
        <div className="card">
          <form onSubmit={handleSendComment}>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Type your reply..."
              rows={4}
            />

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm truncate">{file.name}</span>
                      <span className="text-xs text-gray-500 flex-shrink-0">({formatFileSize(file.size)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-red-400 flex-shrink-0 ml-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between items-center mt-4">
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  className="hidden"
                  accept=".png,.jpg,.jpeg,.gif,.pdf,.txt,.csv,.zip,.json,.xls,.xlsx"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-secondary text-sm"
                >
                  <Paperclip className="w-4 h-4" />
                  Attach File
                </button>
              </div>
              <button
                type="submit"
                disabled={isSending || isUploading || (!newComment.trim() && selectedFiles.length === 0)}
                className="btn btn-primary"
              >
                {isSending || isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {isUploading ? 'Uploading...' : 'Sending...'}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Reply
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {isClosed && (
        <div className="card bg-gray-800/50 text-center py-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="font-semibold mb-2">Ticket Closed</h3>
          <p className="text-gray-400">This ticket has been closed. Create a new ticket if you need further assistance.</p>
        </div>
      )}
    </div>
  )
}
